import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RegistrationData } from '../../pages/RegisterPage';

interface ProviderDetailsStepProps {
  data: RegistrationData;
  onDataChange: (data: Partial<RegistrationData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const skillOptions = [
  'React', 'Vue.js', 'Angular', 'Node.js', 'Python', 'Java', 'C#', 'PHP',
  'WordPress', 'Shopify', 'Web Design', 'UI/UX Design', 'Graphic Design',
  'SEO', 'Digital Marketing', 'Content Writing', 'Translation',
  'Data Analysis', 'Machine Learning', 'Mobile Development', 'DevOps',
  'Project Management', 'Business Consulting', 'Accounting', 'Legal Services'
];

// Removed experienceOptions as we now use bio field instead

const ProviderDetailsStep: React.FC<ProviderDetailsStepProps> = ({
  data,
  onDataChange,
  onNext,
  onBack,
}) => {
  const { t } = useTranslation();

  const handleChange = (field: keyof RegistrationData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    onDataChange({ [field]: event.target.value });
  };

  const handleSkillsChange = (newSkills: string[]) => {
    onDataChange({ skills: newSkills });
  };


  const handleRemoveSkill = (skillToRemove: string) => {
    handleSkillsChange(data.skills?.filter(skill => skill !== skillToRemove) || []);
  };

  const canProceed = data.providerType && data.skills && data.skills.length > 0 && data.bio && data.hourlyRate && 
    (data.providerType === 'FREELANCER' || (data.providerType === 'AGENCY' && data.companyBulstat));

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          textAlign: 'center',
          mb: 4,
          fontWeight: 600,
          color: '#ffffff',
        }}
      >
        {t('auth.register.providerDetails.title')}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
          mb: 4,
          color: '#a0a0a0',
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        {t('auth.register.providerDetails.subtitle')}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl
            fullWidth
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': {
                  borderColor: '#2d2d44',
                },
                '&:hover fieldset': {
                  borderColor: '#28A0F0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#28A0F0',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#a0a0a0',
              },
              '& .MuiSelect-select': {
                color: '#ffffff',
              },
            }}
          >
            <InputLabel>{t('auth.register.providerDetails.providerType')}</InputLabel>
            <Select
              value={data.providerType || ''}
              onChange={handleChange('providerType')}
              label={t('auth.register.providerDetails.providerType')}
            >
              <MenuItem value="FREELANCER">{t('auth.register.providerDetails.freelancer')}</MenuItem>
              <MenuItem value="AGENCY">{t('auth.register.providerDetails.agency')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {data.providerType === 'AGENCY' && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('auth.register.providerDetails.companyBulstat')}
              value={data.companyBulstat || ''}
              onChange={handleChange('companyBulstat')}
              required
              placeholder="123456789"
              inputProps={{ maxLength: 13 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': {
                    borderColor: '#2d2d44',
                  },
                  '&:hover fieldset': {
                    borderColor: '#28A0F0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#28A0F0',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#a0a0a0',
                },
                '& .MuiInputBase-input': {
                  color: '#ffffff',
                },
              }}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
            {t('auth.register.providerDetails.skills')}
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              multiple
              freeSolo
              options={skillOptions}
              value={data.skills || []}
              onChange={(_, newValue) => handleSkillsChange(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    onDelete={() => handleRemoveSkill(option)}
                    sx={{
                      backgroundColor: '#28A0F0',
                      color: '#ffffff',
                      '& .MuiChip-deleteIcon': {
                        color: '#ffffff',
                      },
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('auth.register.providerDetails.skills')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '& fieldset': {
                        borderColor: '#2d2d44',
                      },
                      '&:hover fieldset': {
                        borderColor: '#28A0F0',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#28A0F0',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: '#a0a0a0',
                    },
                    '& .MuiInputBase-input': {
                      color: '#ffffff',
                    },
                  }}
                />
              )}
            />
          </Box>

          <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 3 }}>
            {t('auth.register.providerDetails.skillsHelper')}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('auth.register.providerDetails.bio')}
            value={data.bio || ''}
            onChange={handleChange('bio')}
            required
            multiline
            rows={4}
            placeholder={t('auth.register.providerDetails.bioPlaceholder')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': {
                  borderColor: '#2d2d44',
                },
                '&:hover fieldset': {
                  borderColor: '#28A0F0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#28A0F0',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#a0a0a0',
              },
              '& .MuiInputBase-input': {
                color: '#ffffff',
              },
            }}
          />
          <Typography variant="body2" sx={{ color: '#a0a0a0', mt: 1 }}>
            {t('auth.register.providerDetails.bioHelper')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('auth.register.providerDetails.hourlyRate')}
            type="number"
            value={data.hourlyRate || ''}
            onChange={handleChange('hourlyRate')}
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">лв.</InputAdornment>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': {
                  borderColor: '#2d2d44',
                },
                '&:hover fieldset': {
                  borderColor: '#28A0F0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#28A0F0',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#a0a0a0',
              },
              '& .MuiInputBase-input': {
                color: '#ffffff',
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('auth.register.providerDetails.portfolioUrl')}
            value={data.portfolioUrl || ''}
            onChange={handleChange('portfolioUrl')}
            placeholder={t('auth.register.providerDetails.portfolioPlaceholder')}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& fieldset': {
                  borderColor: '#2d2d44',
                },
                '&:hover fieldset': {
                  borderColor: '#28A0F0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#28A0F0',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#a0a0a0',
              },
              '& .MuiInputBase-input': {
                color: '#ffffff',
              },
            }}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{
            borderColor: '#2d2d44',
            color: '#ffffff',
            px: 4,
            py: 1.5,
            '&:hover': {
              borderColor: '#28A0F0',
              backgroundColor: 'rgba(40, 160, 240, 0.1)',
            },
          }}
        >
          {t('auth.register.providerDetails.back')}
        </Button>

        <Button
          variant="contained"
          onClick={onNext}
          disabled={!canProceed}
          sx={{
            background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
            px: 6,
            py: 1.5,
            '&:hover': {
              background: 'linear-gradient(135deg, #1d4ed8 0%, #be185d 100%)',
            },
            '&:disabled': {
              background: '#2d2d44',
              color: '#6b7280',
            },
          }}
        >
          {t('auth.register.providerDetails.next')}
        </Button>
      </Box>
    </Box>
  );
};

export default ProviderDetailsStep;
